"use client";

import { useEffect, useRef, useState } from "react";

interface ProgressIndicatorProps {
  activeIndex: number;
  duration: number; // ms
}

export function ProgressIndicator({
  activeIndex,
  duration,
}: ProgressIndicatorProps) {
  // Use a key to remount the inner div — guarantees animation restarts
  const [key, setKey] = useState(0);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Bump the key → React unmounts old div and mounts a fresh one
    // The fresh div starts at width:0 and its CSS animation plays from 0→100%
    setKey((k) => k + 1);
  }, [activeIndex]);

  return (
    <div
      style={{
        position: "relative",
        marginTop: "2rem",
        height: "1px",
        width: "120px",
        backgroundColor: "rgba(255,255,255,0.15)",
        overflow: "hidden",
      }}
      role="presentation"
      aria-hidden="true"
    >
      <div
        key={key}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          height: "100%",
          width: "100%",
          backgroundColor: "var(--color-amber, #d99a4e)",
          transformOrigin: "left center",
          animation: `progress-fill ${duration}ms linear forwards`,
        }}
      />

      {/* Keyframes injected once via a style tag */}
      <style>{`
        @keyframes progress-fill {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
}